<template>
    <div class="container admin-table-page">
        <!-- HEADER -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- FILTROS PRINCIPALES -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="filters-container d-flex align-items-center">
                    <b-form-select
                        v-model="selReporte"
                        @change="f_actualiza_reporte"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectReport }}
                        </b-form-select-option>
                        <option v-for="reporte in listReportes" :key="reporte.codigo" :value="reporte.codigo">
                            {{ reporte?.codigo }} - {{ reporte?.nombre }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="selVigencia"
                        @change="f_actualiza_vigencia"
                        class="filter-select ml-2"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectValidity }}
                        </b-form-select-option>
                        <option v-for="vigencia in listVigencias" :key="vigencia.vigencia" :value="vigencia.vigencia">
                            {{ vigencia?.vigencia }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="grupoId"
                        @change="filtraCompaniasPorGrupo"
                        class="filter-select ml-2"
                    >
                        <b-form-select-option value="todos">
                            {{ labels.allGroups }}
                        </b-form-select-option>
                        <option v-for="grupo in listGrupos" :key="grupo.id" :value="grupo.id">
                            {{ grupo?.nombre }}
                        </option>
                    </b-form-select>

                    <b-form-select
                        v-model="companyId"
                        @change="f_actualiza_compania"
                        class="filter-select ml-2"
                        :disabled="filteredCompanies.length === 0"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectCompany }}
                        </b-form-select-option>
                        <option v-for="company in filteredCompanies" :key="company.id" :value="String(company.id)">
                            {{ company?.acronym ? company?.acronym + ' ' : '' }}{{ company?.nombre }}
                        </option>
                    </b-form-select>

                    <b-button
                        class="consult-btn ml-2"
                        @click="consultaORI"
                        :disabled="!isFormValid || isLoading"
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>
            </div>
        </div>

        <!-- TABLA PRINCIPAL -->
        <div class="admin-table-block mt-4">

            <!-- ESTADO INICIAL -->
            <div v-if="!showTablas && !isLoading">
                <CorporateDataTable
                    :items="[]"
                    :fields="[]"
                    :labels="tableLabels"
                    :loading="false"
                    :default-per-page="10"
                    :page-options="[10]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="false"
                    :allow-column-toggle="false"
                    :enable-sorting="false"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                >
                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon
                                :icon="['fas', 'search']"
                                size="3x"
                                class="text-muted mb-3"
                                style="opacity: 0.3;"
                            />
                            <p class="h4 text-muted">
                                {{ labels.selectFilters }}
                            </p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- LOADING -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner></b-spinner>
                <p class="mt-2">{{ labels.loading }}</p>
            </div>

            <!-- TABLA DE FILAS -->
            <div v-if="mostrarTablaFilas && showTablas" class="table-wrapper">
                <CorporateDataTable
                    :items="filaTableData"
                    :fields="filaFields"
                    :labels="filaTableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="filaFilterDefs"
                    :toolbar-disabled="false"
                    :show-pagination="true"
                    :show-rows-per-page="true"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="openCreateFila"
                                :disabled="isVigenciaBloqueada || !consultaRealizada"
                            >
                                {{ labels.createRow }}
                            </b-button>
                            <b-button
                                size="sm"
                                class="reset-btn-danger ml-1"
                                style="height: 31px; min-height: 31px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 13px;"
                                @click="borrarTodasFilas"
                                :disabled="filaTableData.length === 0 || isVigenciaBloqueada || !consultaRealizada"
                            >
                                <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                                {{ labels.deleteAll }}
                            </b-button>
                        </div>
                    </template>

                    <!-- COLUMNA VER -->
                    <template #cell(accionVer)="slotProps">
                        <b-button
                            class="btn-info table-action-btn"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            @click.stop="verFila(slotProps.item)"
                            :disabled="!consultaRealizada"
                            :title="labels.viewDetails"
                        >
                            <font-awesome-icon :icon="['fas', 'eye']" size="xs" />
                        </b-button>
                    </template>

                    <template #cell(accionEdit)="slotProps">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            class="md-fab md-primary"
                            @click.stop="editFila(slotProps.item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs" />
                        </b-button>
                    </template>

                    <template #cell(accionDelete)="{ item }">
                        <b-button
                            class="reset-btn-danger"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            @click.stop="deleteFila(item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'trash']" size="xs" />
                        </b-button>
                    </template>

                    <template #empty>
                        <div class="text-center py-4">
                            <font-awesome-icon :icon="['fas', 'table']" class="empty-table-icon" />
                            <p class="text-muted mt-2">{{ labels.noRows }}</p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- TABLA DE FORMULACIONES (DETALLE) -->
            <div v-if="mostrarDetalle && showTablas" class="table-wrapper">
                <CorporateDataTable
                    :items="detalleTableData"
                    :fields="detalleFields"
                    :labels="detalleTableLabels"
                    :default-per-page="10"
                    :page-options="[10, 20, 50, 100]"
                    :narrow="false"
                    :table-min-height="400"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="detalleFilterDefs"
                    :toolbar-disabled="false"
                    :show-pagination="true"
                    :show-rows-per-page="true"
                    :show-back-button="true"
                    :back-button-text="labels.volver"
                    @back="cerrarDetalle"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn ml-2"
                                @click="abrirModalCrearFormulacion"
                                :disabled="isVigenciaBloqueada || !filaSeleccionadaId || !consultaRealizada"
                            >
                                {{ labels.createFormulation }}
                            </b-button>
                            <b-button
                                size="sm"
                                class="reset-btn-danger ml-1"
                                style="height: 31px; min-height: 31px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; white-space: nowrap; border-radius: 7px; font-size: 13px;"
                                @click="borrarTodasFormulaciones"
                                :disabled="detalleTableData.length === 0 || isVigenciaBloqueada || !consultaRealizada"
                            >
                                <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                                {{ labels.deleteAll }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(contable)="data">
                        <div class="text-center">{{ data.item.contable === 1 ? 'Si' : 'No' }}</div>
                    </template>

                    <template #cell(actual)="data">
                        <div class="text-center">{{ data.item.actual === 1 ? 'Si' : 'No' }}</div>
                    </template>

                    <template #cell(operacion)="data">
                        <div class="text-left">
                            {{ data.item.operacion === '+' ? 'Suma' : data.item.operacion === '-' ? 'Resta' : data.item.operacion }}
                        </div>
                    </template>

                    <template #cell(detalle)="data">
                        <div class="text-left">
                            {{ data.value }}
                        </div>
                    </template>

                    <template #cell(accionEdit)="slotProps">
                        <b-button
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            class="md-fab md-primary"
                            @click.stop="editDetalle(slotProps.item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs" />
                        </b-button>
                    </template>

                    <template #cell(accionDelete)="{ item }">
                        <b-button
                            class="reset-btn-danger"
                            style="width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border-radius: 4px; margin: 0 auto;"
                            @click.stop="deleteDetalle(item)"
                            :disabled="isVigenciaBloqueada || !consultaRealizada"
                        >
                            <font-awesome-icon :icon="['fas', 'trash']" size="xs" />
                        </b-button>
                    </template>

                    <template #empty>
                        <div class="text-center py-4">
                            <font-awesome-icon :icon="['fas', 'table']" class="empty-table-icon" />
                            <p class="text-muted mt-2">{{ labels.noDetail }}</p>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>
        </div>

        <!-- MODAL CREAR/EDITAR FILA -->
        <b-modal
            :model-value="showModFila"
            :no-close-on-backdrop="true"
            hide-footer
            hide-header-close
            centered
            size="md"
            :title="isEditingFila ? labels.editRow : labels.createRow"
            header-class="reset-modal-header"
            title-class="reset-modal-title"
            body-class="reset-modal-body"
        >
            <div class="uniform-modal-body">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">{{ isEditingFila ? labels.editRow : labels.createRow }}</div>
                    <div class="password-policy-subtitle">{{ isEditingFila ? 'Modifica los datos de la fila.' : 'Ingresa los datos de la nueva fila.' }}</div>
                </div>

                <div class="uniform-form-row d-flex align-items-center mb-3">
                    <div class="col-md-4 uniform-label-col">
                        <label class="field-label required">{{ labels.order }}</label>
                    </div>
                    <div class="col-md-8">
                        <input
                            v-model="filaFormData.orden"
                            type="number"
                            min="1"
                            class="form-control form-control-sm form-input"
                            :class="{ 'is-invalid': !filaFormData.orden && touchedFila }"
                            @blur="touchedFila = true"
                        />
                        <div v-if="!filaFormData.orden && touchedFila" class="invalid-feedback">{{ labels.requiredField }}</div>
                    </div>
                </div>

                <div class="uniform-form-row d-flex align-items-center mb-3">
                    <div class="col-md-4 uniform-label-col">
                        <label class="field-label">{{ labels.description }}</label>
                    </div>
                    <div class="col-md-8">
                        <input
                            v-model="filaFormData.descripcion"
                            maxlength="100"
                            class="form-control form-control-sm form-input"
                            placeholder="Ingrese una descripción..."
                        />
                    </div>
                </div>

                <div class="reset-dialog-actions d-flex justify-content-end mt-4">
                    <b-button class="reset-btn-secondary" @click="cerrarModalFila">{{ labels.cancel }}</b-button>
                    <b-button class="reset-btn-primary ml-2" @click="guardarFila" :disabled="!filaFormData.orden || isVigenciaBloqueada || isLoading">
                        {{ labels.save }}
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- ============================================================ -->
        <!-- MODAL CREAR/EDITAR FORMULACIÓN (DETALLE) - VERSIÓN COMPLETA   -->
        <!-- ============================================================ -->
        <b-modal
            :model-value="showModDetalle"
            :no-close-on-backdrop="true"
            hide-footer
            hide-header-close
            centered
            size="lg"
            :title="isEditingDetalle ? labels.editFormulation : labels.createFormulation"
            header-class="reset-modal-header"
            title-class="reset-modal-title"
            body-class="reset-modal-body"
        >
            <div class="uniform-modal-body">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">{{ isEditingDetalle ? labels.editFormulation : labels.createFormulation }}</div>
                    <div class="password-policy-subtitle">{{ isEditingDetalle ? 'Modifica los datos de la formulación.' : 'Ingresa los datos de la nueva formulación.' }}</div>
                </div>

                <!-- Actual -->
                <b-row class="d-flex align-items-center uniform-form-row">
                    <b-col cols="12" md="4" class="text-left uniform-label-col">
                        <label class="form-label">{{ labels.actual }}</label>
                    </b-col>
                    <b-col cols="12" md="8" class="text-left">
                        <b-form-select v-model="detalleFormData.actual" class="form-input" :disabled="isVigenciaBloqueada">
                            <option value="">{{ labels.select }}</option>
                            <option value="1">{{ labels.yes }}</option>
                            <option value="0">{{ labels.no }}</option>
                        </b-form-select>
                    </b-col>
                </b-row>

                <!-- Operación -->
                <b-row class="d-flex align-items-center uniform-form-row">
                    <b-col cols="12" md="4" class="text-left uniform-label-col">
                        <label class="form-label">{{ labels.operacion }}</label>
                    </b-col>
                    <b-col cols="12" md="8" class="text-left">
                        <b-form-select v-model="detalleFormData.operacion" class="form-input" :disabled="isVigenciaBloqueada">
                            <option value="">{{ labels.select }}</option>
                            <option value="+">Suma</option>
                            <option value="-">Resta</option>
                        </b-form-select>
                    </b-col>
                </b-row>

                <!-- Contable -->
                <b-row class="d-flex align-items-center uniform-form-row">
                    <b-col cols="12" md="4" class="text-left uniform-label-col">
                        <label class="form-label">{{ labels.contable }}</label>
                    </b-col>
                    <b-col cols="12" md="8" class="text-left">
                        <b-form-select v-model="detalleFormData.contable" class="form-input" :disabled="isVigenciaBloqueada">
                            <option value="">{{ labels.select }}</option>
                            <option value="1">{{ labels.yes }}</option>
                            <option value="0">{{ labels.no }}</option>
                        </b-form-select>
                    </b-col>
                </b-row>

                <!-- Criterio Cuenta -->
                <template v-if="showCuenta">
                    <b-row class="d-flex align-items-center uniform-form-row">
                        <b-col cols="12" md="4" class="text-left uniform-label-col">
                            <label class="form-label">{{ labels.criterio }}</label>
                        </b-col>
                        <b-col cols="12" md="8" class="text-left">
                            <div class="criterio-inputs-wrapper">
                                <b-form-input 
                                    v-model="critCuenta" 
                                    type="text" 
                                    class="form-input"
                                    style="height: 34px; font-size: 13px; margin-bottom: 6px;"
                                    autocomplete="off"
                                    :placeholder="labels.criterioCuentaPlaceholder"
                                    @input="buscaCuenta"
                                    :disabled="isVigenciaBloqueada"
                                />
                                <b-form-input 
                                    v-model="critDescripcion" 
                                    type="text" 
                                    class="form-input"
                                    style="height: 34px; font-size: 13px;"
                                    autocomplete="off"
                                    :placeholder="labels.criterioDescripcionPlaceholder"
                                    @input="buscaDescripcion"
                                    :disabled="isVigenciaBloqueada"
                                />
                            </div>
                        </b-col>
                    </b-row>

                    <!-- Tabla de Cuentas -->
                    <div class="accounts-section mt-2">
                        <div v-if="listCuentasSelMul.length > 0" class="accounts-list mt-1">
                            <div class="select-all-container mb-2 d-flex align-items-center justify-content-between">
                                <b-button 
                                    size="sm"
                                    style="background-color: #2e507a; border-color: #2e507a; font-size: 13px; padding: 4px 16px; border-radius: 4px; color: white;"
                                    @click="toggleSelectAllCuentas"
                                    :disabled="isVigenciaBloqueada"
                                >
                                    {{ selectAllCuentas ? labels.deselectAll : labels.selectAll }}
                                </b-button>
                                <span style="font-size: 13px; color: #6c757d;">
                                    {{ labels.selected }}: <strong>{{ listadoCuentas.length }}</strong> / {{ listCuentasSelMul.length }}
                                </span>
                            </div>

                            <div class="custom-scroll-table">
                                <table class="table table-sm table-hover" style="font-size: 13px; margin-bottom: 0;">
                                    <thead style="position: sticky; top: 0; background: #f8f9fa; z-index: 1;">
                                        <tr>
                                            <th style="width: 120px; font-weight: 600; font-size: 12px; color: #495057; padding: 6px 8px;">
                                                {{ labels.account }}
                                            </th>
                                            <th style="font-weight: 600; font-size: 12px; color: #495057; padding: 6px 8px;">
                                                {{ labels.description }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr 
                                            v-for="cuenta in listCuentasSelMul" 
                                            :key="cuenta.id"
                                            @click="!isVigenciaBloqueada && toggleCuentaSelection(cuenta)"
                                            :class="{'table-active-blue': isCuentaSelected(cuenta)}"
                                            :style="isVigenciaBloqueada ? 'cursor: default;' : 'cursor: pointer;'"
                                        >
                                            <td style="padding: 5px 8px; border-radius: 8px 0 0 8px;">
                                                <strong>{{ cuenta?.cuenta || '' }}</strong>
                                            </td>
                                            <td style="padding: 5px 8px; border-radius: 0 8px 8px 0;" :title="cuenta.descripcion">
                                                {{ cuenta?.descripcion || '' }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div v-else-if="!loadingCuentas" class="text-center py-2" style="height: 150px; display: flex; align-items: center; justify-content: center;">
                            <span class="text-muted" style="font-size: 13px;">{{ labels.noAccountsFound }}</span>
                        </div>

                        <div v-else class="text-center py-2" style="height: 150px; display: flex; align-items: center; justify-content: center;">
                            <b-spinner small class="mr-1"></b-spinner>
                            <span style="font-size: 13px;">{{ labels.loadingAccounts }}</span>
                        </div>
                    </div>
                </template>

                <!-- Formulación -->
                <b-row class="d-flex align-items-center uniform-form-row" v-if="showFormulacion">
                    <b-col cols="12" md="4" class="text-left uniform-label-col">
                        <label class="form-label">{{ labels.formulacion }}</label>
                    </b-col>
                    <b-col cols="12" md="8" class="text-left">
                        <div class="d-flex flex-wrap align-items-center gap-3">
                            <div class="custom-checkbox-wrapper">
                                <input 
                                    type="radio"
                                    id="chk_sf_new"
                                    value="sf"
                                    v-model="detalleFormData.chk"
                                    @change="actualizarVistaFormulacion(detalleFormData.chk)"
                                    :disabled="isVigenciaBloqueada"
                                />
                                <label for="chk_sf_new">Fila</label>
                            </div>
                            <div class="custom-checkbox-wrapper">
                                <input 
                                    type="radio"
                                    id="chk_sc_new"
                                    value="sc"
                                    v-model="detalleFormData.chk"
                                    @change="actualizarVistaFormulacion(detalleFormData.chk)"
                                    :disabled="isVigenciaBloqueada"
                                />
                                <label for="chk_sc_new">Columna</label>
                            </div>
                            <div class="custom-checkbox-wrapper">
                                <input 
                                    type="radio"
                                    id="chk_c_new"
                                    value="c"
                                    v-model="detalleFormData.chk"
                                    @change="actualizarVistaFormulacion(detalleFormData.chk)"
                                    :disabled="isVigenciaBloqueada"
                                />
                                <label for="chk_c_new">Celda</label>
                            </div>
                            <div class="custom-checkbox-wrapper">
                                <input 
                                    type="radio"
                                    id="chk_n_new"
                                    value="n"
                                    v-model="detalleFormData.chk"
                                    @change="actualizarVistaFormulacion(detalleFormData.chk)"
                                    :disabled="isVigenciaBloqueada"
                                />
                                <label for="chk_n_new">Niif</label>
                            </div>
                            <div class="custom-checkbox-wrapper" v-if="administrador">
                                <input 
                                    type="radio"
                                    id="chk_m_new"
                                    value="m"
                                    v-model="detalleFormData.chk"
                                    @change="actualizarVistaFormulacion(detalleFormData.chk)"
                                    :disabled="isVigenciaBloqueada"
                                />
                                <label for="chk_m_new">Manual</label>
                            </div>
                            <div class="custom-checkbox-wrapper" v-if="administrador">
                                <input 
                                    type="radio"
                                    id="chk_cons_new"
                                    value="cons"
                                    v-model="detalleFormData.chk"
                                    @change="actualizarVistaFormulacion(detalleFormData.chk)"
                                    :disabled="isVigenciaBloqueada"
                                />
                                <label for="chk_cons_new">Ajuste Consol</label>
                            </div>
                            <div class="custom-checkbox-wrapper" v-if="administrador">
                                <input 
                                    type="radio"
                                    id="chk_ac_new"
                                    value="ac"
                                    v-model="detalleFormData.chk"
                                    @change="actualizarVistaFormulacion(detalleFormData.chk)"
                                    :disabled="isVigenciaBloqueada"
                                />
                                <label for="chk_ac_new">Ajuste Combinado</label>
                            </div>
                        </div>
                    </b-col>
                </b-row>

                <!-- Suma Fila -->
                <b-row class="d-flex align-items-center uniform-form-row" v-if="showSumaFila">
                    <b-col cols="12" md="4" class="text-left uniform-label-col">
                        <label class="form-label">Suma Fila:</label>
                    </b-col>
                    <b-col cols="12" md="8" class="text-left">
                        <b-form-input v-model="detalleFormData.sumFila" type="text" class="form-input" :disabled="isVigenciaBloqueada" />
                    </b-col>
                </b-row>

                <!-- Suma Columna -->
                <b-row class="d-flex align-items-center uniform-form-row" v-if="showSumaColumna">
                    <b-col cols="12" md="4" class="text-left uniform-label-col">
                        <label class="form-label">Suma Columna:</label>
                    </b-col>
                    <b-col cols="12" md="8" class="text-left">
                        <b-form-input v-model="detalleFormData.sumColumna" type="text" class="form-input" :disabled="isVigenciaBloqueada" />
                    </b-col>
                </b-row>

                <!-- Celda -->
                <b-row class="d-flex align-items-center uniform-form-row" v-if="showCelda">
                    <b-col cols="12" md="4" class="text-left uniform-label-col">
                        <label class="form-label">Celda:</label>
                    </b-col>
                    <b-col cols="12" md="8" class="text-left">
                        <div class="d-flex gap-2">
                            <b-form-input v-model="detalleFormData.celFila" type="number" class="form-input" style="max-width: 100px;" :disabled="isVigenciaBloqueada" />
                            <b-form-input v-model="detalleFormData.celColumna" type="number" class="form-input" style="max-width: 100px;" :disabled="isVigenciaBloqueada" />
                        </div>
                    </b-col>
                </b-row>

                <!-- Niif -->
                <template v-if="showNiif">
                    <b-row class="d-flex align-items-center uniform-form-row">
                        <b-col cols="12" md="4" class="text-left uniform-label-col">
                            <label class="form-label" style="font-size: 14px; font-weight: 500; color: #495057;">
                                {{ labels.niifFilter }}
                            </label>
                        </b-col>
                        <b-col cols="12" md="8" class="text-left">
                            <div class="criterio-inputs-wrapper">
                                <b-form-input 
                                    v-model="critNiifCode" 
                                    type="text" 
                                    class="form-input"
                                    style="height: 34px; font-size: 13px; margin-bottom: 6px;"
                                    autocomplete="off"
                                    :placeholder="labels.niifCodePlaceholder"
                                    @input="filtraNiifs"
                                    :disabled="isVigenciaBloqueada"
                                />
                                <b-form-input 
                                    v-model="critNiifName" 
                                    type="text" 
                                    class="form-input"
                                    style="height: 34px; font-size: 13px;"
                                    autocomplete="off"
                                    :placeholder="labels.niifNamePlaceholder"
                                    @input="filtraNiifs"
                                    :disabled="isVigenciaBloqueada"
                                />
                            </div>
                        </b-col>
                    </b-row>

                    <div class="accounts-section mt-2">
                        <div v-if="listNiifFiltered.length > 0" class="accounts-list mt-1">
                            <div class="select-all-container mb-2 d-flex align-items-center justify-content-between">
                                <b-button 
                                    size="sm"
                                    style="background-color: #2e507a; border-color: #2e507a; font-size: 13px; padding: 4px 16px; border-radius: 4px; color: white;"
                                    @click="toggleSelectAllNiifs"
                                    :disabled="isVigenciaBloqueada"
                                >
                                    {{ selectAllNiifs ? labels.deselectAll : labels.selectAll }}
                                </b-button>
                                <span style="font-size: 13px; color: #6c757d;">
                                    {{ labels.selected }}: <strong>{{ detalleFormData.arrNiif.length }}</strong> / {{ listNiifFiltered.length }}
                                </span>
                            </div>

                            <div class="custom-scroll-table">
                                <table class="table table-sm table-hover" style="font-size: 13px; margin-bottom: 0;">
                                    <thead style="position: sticky; top: 0; background: #f8f9fa; z-index: 1;">
                                        <tr>
                                            <th style="width: 120px; font-weight: 600; font-size: 12px; color: #495057; padding: 6px 8px;">
                                                {{ labels.code }}
                                            </th>
                                            <th style="font-weight: 600; font-size: 12px; color: #495057; padding: 6px 8px;">
                                                {{ labels.niifName }}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr 
                                            v-for="niif in listNiifFiltered" 
                                            :key="niif.id"
                                            @click="!isVigenciaBloqueada && toggleNiifSelection(niif)"
                                            :class="{'table-active-blue': isNiifSelected(niif)}"
                                            :style="isVigenciaBloqueada ? 'cursor: default;' : 'cursor: pointer;'"
                                        >
                                            <td style="padding: 5px 8px; border-radius: 8px 0 0 8px;">
                                                <strong>{{ niif?.niif || '' }}</strong>
                                            </td>
                                            <td style="padding: 5px 8px; border-radius: 0 8px 8px 0;" :title="niif.nameNiif">
                                                {{ niif?.nameNiif || '' }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div v-else-if="listNiif.length > 0 && !loadingNiifs" class="text-center py-2" style="height: 150px; display: flex; align-items: center; justify-content: center;">
                            <span class="text-muted" style="font-size: 13px;">{{ labels.noNiifsMatch }}</span>
                        </div>

                        <div v-else-if="loadingNiifs" class="text-center py-2" style="height: 150px; display: flex; align-items: center; justify-content: center;">
                            <b-spinner small class="mr-1"></b-spinner>
                            <span style="font-size: 13px;">{{ labels.loadingNiifs }}</span>
                        </div>

                        <div v-else class="text-center py-2" style="height: 150px; display: flex; align-items: center; justify-content: center;">
                            <span class="text-muted" style="font-size: 13px;">{{ labels.noNiifsAvailable }}</span>
                        </div>
                    </div>
                </template>

                <div class="reset-dialog-actions d-flex justify-content-end mt-4 uniform-actions-wrap">
                    <b-button class="reset-btn-secondary" @click="cerrarModalDetalle">
                        {{ labels.cancel }}
                    </b-button>
                    <b-button class="reset-btn-primary ml-2" @click="guardarDetalle" :disabled="!isFormulacionValid || isVigenciaBloqueada || isLoading">
                        {{ labels.save }}
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- MODAL CONFIRMACIÓN ELIMINAR -->
        <b-modal
            :model-value="showConfirmDeleteModal"
            :no-close-on-backdrop="true"
            hide-footer
            hide-header-close
            centered
            size="md"
            :title="labels.confirmDelete"
            header-class="reset-modal-header"
            title-class="reset-modal-title"
            body-class="reset-modal-body"
        >
            <div class="uniform-modal-body">
                <div class="password-policy-box mb-4">
                    <div class="password-policy-title">{{ labels.areYouSure }}</div>
                    <div class="password-policy-subtitle">{{ labels.confirmDeleteText }}</div>
                </div>
                <div class="reset-dialog-actions d-flex justify-content-end mt-4">
                    <b-button class="reset-btn-secondary" @click="closeConfirmDeleteModal">{{ labels.cancel }}</b-button>
                    <b-button class="reset-btn-primary ml-2" @click="executeDelete" style="background-color: #cd2838; border-color: #cd2838;">
                        {{ labels.confirm }}
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import Balance from '../../services/BalanceNiif'
import PlanCuentas from '../../services/PlanCuentas'
import PlanConsolidacion from '../../services/PlanConsolidacion'
import Company from '../../services/Company'
import Grupo from '../../services/Grupo'
import CorporateDataTable from '../utils/CorporateDataTable.vue'

export default {
    name: 'OriReporte',
    components: {
        CorporateDataTable
    },
    data: () => ({
        administrador: false,
        prellenandoFormulario: false,
        selReporte: '',
        selVigencia: '',
        companyId: '',
        grupoId: 'todos',

        listVigencias: [],
        listCompanies: [],
        listGrupos: [],
        filteredCompanies: [],
        listReportes: [],
        companiaUsuario: [],

        isLoading: false,
        isLoadingDetalle: false,

        showTablas: false,
        consultaRealizada: false,

        vigencia_anterior: '',
        compania_anterior: '',
        codReporteAnterior: '',

        listFila: [],

        detalleData: [],
        filaSeleccionadaId: null,
        filaSeleccionadaOrden: null,

        showModFila: false,
        showModDetalle: false,
        isEditingFila: false,
        isEditingDetalle: false,
        touchedFila: false,
        touchedDetalle: false,

        filaFormData: {
            id: null,
            orden: null,
            descripcion: ''
        },

        // ===== DATOS DEL MODAL DE FORMULACIÓN =====
        detalleFormData: {
            id: null,
            contable: null,
            actual: null,
            operacion: null,
            chk: '',
            sumFila: '',
            sumColumna: '',
            celFila: '',
            celColumna: '',
            arrNiif: [],
            cuentasSeleccionadas: []
        },

        // Cuentas
        showCuenta: false,
        listCuentas: [],
        listCuentasSelMul: [],
        listadoCuentas: [],
        selectAllCuentas: false,
        loadingCuentas: false,
        critCuenta: '',
        critDescripcion: '',

        // Formulación
        showFormulacion: false,
        showSumaFila: false,
        showSumaColumna: false,
        showCelda: false,
        showNiif: false,

        // NIIF
        listNiif: [],
        listNiifFiltered: [],
        selectAllNiifs: false,
        loadingNiifs: false,
        critNiifCode: '',
        critNiifName: '',

        showConfirmDeleteModal: false,
        deleteId: null,
        isDeleteAll: false,
        deleteTarget: 'fila',

        infoVigeCom: {
            vigencia: "",
            companyId: "",
            compania: "",
            codReporte: ""
        },
    }),

    computed: {
        labels() {
            const es = this.$store.state._language == "es-ES"
            return {
                title: es ? 'Reporte ORI' : 'ORI Report',
                selectReport: es ? 'Seleccione Reporte' : 'Select Report',
                selectValidity: es ? 'Seleccione Vigencia' : 'Select Validity',
                selectCompany: es ? 'Seleccione Compañía' : 'Select Company',
                allGroups: es ? 'Todos los grupos' : 'All groups',
                consultar: es ? 'Consultar' : 'Consult',
                selectFilters: es ? 'Seleccione los filtros y consulte' : 'Select filters and consult',
                loading: es ? 'Cargando...' : 'Loading...',
                createRow: es ? 'Crear Fila' : 'Create Row',
                createFormulation: es ? 'Crear Formulación' : 'Create Formulation',
                deleteAll: es ? 'Eliminar Todo' : 'Delete All',
                edit: es ? 'Editar' : 'Edit',
                delete: es ? 'Eliminar' : 'Delete',
                codigo: es ? 'Código' : 'Code',
                descripcion: es ? 'Descripción' : 'Description',
                noRows: es ? 'No hay filas disponibles' : 'No rows available',
                confirmDelete: es ? 'Confirmar Eliminación' : 'Confirm Delete',
                areYouSure: es ? '¿Está seguro?' : 'Are you sure?',
                confirmDeleteText: es ? '¿Está seguro que desea eliminar este registro?' : 'Are you sure you want to delete this record?',
                cancel: es ? 'Cancelar' : 'Cancel',
                confirm: es ? 'Confirmar' : 'Confirm',
                noMatches: es ? 'No se encontraron resultados' : 'No results found',
                searchPlaceholder: es ? 'Buscar en la tabla...' : 'Search in table...',
                rowsPerPage: es ? 'Filas por página:' : 'Rows per page:',
                noSort: es ? 'Sin orden' : 'No sorting',
                sortAsc: es ? 'A-Z / Menor a mayor' : 'A-Z / Low to high',
                sortDesc: es ? 'Z-A / Mayor a menor' : 'Z-A / High to low',
                columns: es ? 'Columnas' : 'Columns',
                showAllColumns: es ? 'Mostrar todas' : 'Show all',
                filters: es ? 'Filtros' : 'Filters',
                clearFilters: es ? 'Limpiar filtros' : 'Clear filters',
                detail: es ? 'Detalle' : 'Detail',
                contable: es ? 'Contable' : 'Accountant',
                cuenta: es ? 'Cuenta' : 'Account',
                actual: es ? 'Actual' : 'Actual',
                operacion: es ? 'Operación' : 'Operation',
                formulacion: es ? 'Formulación' : 'Formulation',
                noDetail: es ? 'No hay formulaciones disponibles' : 'No formulations available',
                order: es ? 'Orden' : 'Order',
                save: es ? 'Guardar' : 'Save',
                editRow: es ? 'Editar Fila' : 'Edit Row',
                editFormulation: es ? 'Editar Formulación' : 'Edit Formulation',
                requiredField: es ? 'Este campo es requerido' : 'This field is required',
                viewDetails: es ? 'Ver formulaciones' : 'View formulations',
                volver: es ? 'Volver' : 'Back',
                ver: es ? 'Ver' : 'View',
                // Labels para el modal
                select: es ? 'Seleccione...' : 'Select...',
                yes: es ? 'Sí' : 'Yes',
                no: es ? 'No' : 'No',
                criterio: es ? 'Criterio:' : 'Criterion:',
                criterioCuentaPlaceholder: es ? 'Buscar por cuenta...' : 'Search by account...',
                criterioDescripcionPlaceholder: es ? 'Buscar por descripción...' : 'Search by description...',
                account: es ? 'Cuenta:' : 'Account:',
                selected: es ? 'Seleccionadas' : 'Selected',
                selectAll: es ? 'Seleccionar todas' : 'Select all',
                deselectAll: es ? 'Deseleccionar todas' : 'Deselect all',
                noAccountsFound: es ? 'No se encontraron cuentas' : 'No accounts found',
                loadingAccounts: es ? 'Cargando cuentas...' : 'Loading accounts...',
                niifFilter: es ? 'Filtro NIIF' : 'NIIF Filter',
                niifCodePlaceholder: es ? 'Buscar por código...' : 'Search by code...',
                niifNamePlaceholder: es ? 'Buscar por nombre...' : 'Search by name...',
                code: es ? 'Código' : 'Code',
                niifName: es ? 'Nombre NIIF' : 'NIIF Name',
                noNiifsAvailable: es ? 'No hay NIIF disponibles' : 'No NIIF available',
                noNiifsMatch: es ? 'No hay NIIF que coincidan con el filtro' : 'No NIIF match the filter',
                loadingNiifs: es ? 'Cargando NIIF...' : 'Loading NIIF...'
            }
        },

        isFormValid() {
            return this.selReporte && this.selVigencia && this.companyId
        },

        isVigenciaBloqueada() {
            return this.selVigencia ? !this.$store.getters.isPeriodoEnabled(this.selVigencia) : false
        },

        mostrarTablaFilas() {
            return !this.filaSeleccionadaId
        },

        mostrarDetalle() {
            return this.filaSeleccionadaId !== null
        },

        filaTableData() {
            const filas = Array.isArray(this.listFila) ? this.listFila : []
            return filas.map(item => ({
                id: item.id,
                codigo: item.orden,
                descripcion: item.descripcion
            }))
        },

        detalleTableData() {
            if (!this.detalleData || !Array.isArray(this.detalleData)) return []
            return this.detalleData.map(item => ({
                id: item.id,
                contable: item.contable,
                actual: item.actual,
                operacion: item.operacion || '-',
                detalle: this.getDetalleTexto(item)
            }))
        },

        filaFields() {
            return [
                { key: 'codigo', label: this.labels.codigo, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'descripcion', label: this.labels.descripcion, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'accionVer', label: this.labels.ver, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false },
                { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false },
                { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
            ]
        },

        detalleFields() {
            return [
                { key: 'contable', label: this.labels.contable, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'actual', label: this.labels.actual, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle' },
                { key: 'operacion', label: this.labels.operacion, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'detalle', label: this.labels.detail, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle' },
                { key: 'accionEdit', label: this.labels.edit, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false },
                { key: 'accionDelete', label: this.labels.delete, thClass: 'text-center', tdClass: 'text-center align-middle', sortable: false }
            ]
        },

        filaFilterDefs() {
            return [
                { key: 'codigo', type: 'text', placeholder: 'Filtrar código...' },
                { key: 'descripcion', type: 'text', placeholder: 'Filtrar descripción...' }
            ]
        },

        detalleFilterDefs() {
            return [
                { key: 'contable', type: 'text', placeholder: 'Filtrar contable...' },
                { key: 'actual', type: 'text', placeholder: 'Filtrar actual...' },
                { key: 'operacion', type: 'text', placeholder: 'Filtrar operación...' },
                { key: 'detalle', type: 'text', placeholder: 'Filtrar detalle...' }
            ]
        },

        tableLabels() {
            return {
                noRow: this.labels.noRows,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },

        filaTableLabels() {
            return {
                noRow: this.labels.noRows,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },

        detalleTableLabels() {
            return {
                noRow: this.labels.noDetail,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },

        // Validación del formulario de detalle
        isFormulacionValid() {
            if (!this.detalleFormData.contable) return false
            if (!this.detalleFormData.actual) return false
            if (!this.detalleFormData.operacion) return false

            if (this.detalleFormData.contable === 1) {
                if (this.listadoCuentas.length === 0) {
                    return false
                }
            }

            if (this.detalleFormData.contable === 0 && !this.detalleFormData.chk) {
                return false
            }

            return true
        }
    },

    watch: {
        selVigencia(valNew) {
            if (valNew !== this.vigencia_anterior) {
                this.vigencia_anterior = valNew
                if (this.isVigenciaBloqueada) {
                    this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                }
            }
        },

        // ===== WATCH DEL MODAL DE FORMULACIÓN =====
        'detalleFormData.contable'(valNew) {
            if (this.prellenandoFormulario) return
            if (valNew == 0) {
                this.showCuenta = false
                this.showFormulacion = true
                this.listCuentas = []
                this.listCuentasSelMul = []
                this.listadoCuentas = []
                this.showNiif = false
                this.detalleFormData.chk = ''
            } else if (valNew == 1) {
                this.showCuenta = true
                this.showFormulacion = false
                this.showSumaFila = false
                this.showNiif = false
                this.detalleFormData.chk = ''
                this.detalleFormData.arrNiif = []
                this.consCuentas()
            }
        },

        'detalleFormData.actual'(valNewAct, valOldAct) {
            if (this.prellenandoFormulario) return
            if (valNewAct != valOldAct && this.detalleFormData.contable == 1) {
                this.showCuenta = false
                this.listadoCuentas = []
                this.consCuentas()
            }
        },

        'detalleFormData.operacion'(valNewOpe, valOldOpe) {
            if (this.prellenandoFormulario) return
            if (valNewOpe != valOldOpe && this.detalleFormData.contable == 1) {
                this.showCuenta = false
                this.listadoCuentas = []
                this.consCuentas()
            }
        },

        'detalleFormData.chk'(valNew) {
            if (this.prellenandoFormulario) return
            this.listNiif = []

            if (valNew == 'n') {
                this.cargarNiif()
                this.showNiif = true
                this.showSumaFila = false
                this.showSumaColumna = false
                this.showCelda = false

                this.detalleFormData.sumFila = ''
                this.detalleFormData.sumColumna = ''
                this.detalleFormData.celFila = ''
                this.detalleFormData.celColumna = ''
            } else if (valNew == 'sf') {
                this.showSumaFila = true
                this.showSumaColumna = false
                this.showCelda = false
                this.showNiif = false

                this.detalleFormData.arrNiif = []
                this.detalleFormData.sumColumna = ''
                this.detalleFormData.celFila = ''
                this.detalleFormData.celColumna = ''
            } else if (valNew == 'sc') {
                this.showSumaColumna = true
                this.showSumaFila = false
                this.showCelda = false
                this.showNiif = false

                this.detalleFormData.arrNiif = []
                this.detalleFormData.sumFila = ''
                this.detalleFormData.celFila = ''
                this.detalleFormData.celColumna = ''
            } else if (valNew == 'c') {
                this.showCelda = true
                this.showSumaFila = false
                this.showSumaColumna = false
                this.showNiif = false

                this.detalleFormData.arrNiif = []
                this.detalleFormData.sumFila = ''
                this.detalleFormData.sumColumna = ''
            } else if (valNew == 'm' || valNew == 'cons' || valNew == 'ac') {
                this.showSumaFila = false
                this.showSumaColumna = false
                this.showNiif = false
                this.showCelda = false

                this.detalleFormData.arrNiif = []
                this.detalleFormData.sumFila = ''
                this.detalleFormData.sumColumna = ''
                this.detalleFormData.celFila = ''
                this.detalleFormData.celColumna = ''
            }
        }
    },

    methods: {
        goBack() {
            this.$router.back()
        },

        filtraCompaniasPorGrupo(valor = null) {
            const grupoIdSeleccionado = valor !== null ? valor : this.grupoId
            this.onFiltroChange()
            this.companyId = ''

            if (!grupoIdSeleccionado || grupoIdSeleccionado === 'todos') {
                this.filteredCompanies = [...this.listCompanies]
                return
            }

            const grupoIdStr = grupoIdSeleccionado.toString()
            this.filteredCompanies = this.listCompanies.filter(company => {
                if (!company?.id_grupos) return false
                const gruposCompania = company.id_grupos.split(',').map(id => id.trim()).filter(id => id !== '')
                return gruposCompania.includes(grupoIdStr)
            })
        },

        f_actualiza_compania() {
            if (this.companyId) {
                this.resetData()
                this.compania_anterior = this.companyId
            }
        },

        f_actualiza_reporte() {
            if (this.codReporteAnterior != this.selReporte) {
                this.resetData()
                this.codReporteAnterior = this.selReporte
            }
        },

        f_actualiza_vigencia() {
            if (this.vigencia_anterior != this.selVigencia) {
                this.resetData()
                this.vigencia_anterior = this.selVigencia
            }
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
            }
        },

        onFiltroChange() {
            this.showTablas = false
            this.consultaRealizada = false
            this.showModFila = false
            this.showModDetalle = false
            this.listFila = []
            this.detalleData = []
            this.filaSeleccionadaId = null
            this.filaSeleccionadaOrden = null
        },

        resetData() {
            this.showTablas = false
            this.consultaRealizada = false
            this.showModFila = false
            this.showModDetalle = false
            this.listFila = []
            this.detalleData = []
            this.filaSeleccionadaId = null
            this.filaSeleccionadaOrden = null
        },

        consultaORI() {
            if (!this.isFormValid) {
                this.$toast.error("Diligenciar los campos: reporte, vigencia y compañía")
                return
            }

            this.isLoading = true
            this.consultaRealizada = true

            this.infoVigeCom = {
                vigencia: this.selVigencia,
                companyId: this.companyId,
                compania: this.companyId,
                codReporte: this.selReporte
            }

            this.cargarFilas()
        },

        cargarFilas() {
            this.isLoading = true

            Balance.obtenerFilasPrincipalesOri(this.selReporte, this.companyId, this.selVigencia)
                .then((res) => {
                    let filas = res?.data?.data || res?.data?.filas || []
                    if (filas.length > 0 && typeof filas[0] === 'object' && !Array.isArray(filas[0])) {
                        const valores = Object.values(filas[0])
                        filas = valores.filter(item => item && item.id)
                    }
                    this.listFila = Array.isArray(filas) ? filas : []
                    this.detalleData = []
                    this.filaSeleccionadaId = null
                    this.filaSeleccionadaOrden = null

                    this.showTablas = true
                    this.consultaRealizada = true
                })
                .catch((err) => {
                    console.error(err)
                    this.listFila = []
                    this.detalleData = []
                    this.$toast.error('Error al cargar los datos')
                })
                .finally(() => {
                    this.isLoading = false
                })
        },

        verFila(item) {
            const fila = this.listFila.find(f => f.id === item.id)
            if (!fila) {
                this.$toast.error('No se encontró la fila')
                return
            }
            this.filaSeleccionadaId = fila.id
            this.filaSeleccionadaOrden = fila.orden
            this.cargarDetalle()
        },

        cerrarDetalle() {
            this.filaSeleccionadaId = null
            this.filaSeleccionadaOrden = null
            this.detalleData = []
            this.showModDetalle = false
        },

        cargarDetalle() {
            if (!this.filaSeleccionadaId || !this.filaSeleccionadaOrden) {
                this.detalleData = []
                return
            }

            this.isLoadingDetalle = true

            Balance.obtenerFormulacionesOri(
                this.selReporte,
                this.companyId,
                this.selVigencia,
                this.filaSeleccionadaOrden
            )
            .then((res) => {
                if (res.status === 200) {
                    this.detalleData = res?.data?.data || res?.data?.formulaciones || []
                } else if (res.status === 204) {
                    this.detalleData = []
                }
            })
            .catch((err) => {
                console.error(err)
                if (err.response?.status === 404 || err.response?.status === 204) {
                    this.detalleData = []
                } else {
                    this.detalleData = []
                    this.$toast.error('Error al cargar las formulaciones')
                }
            })
            .finally(() => {
                this.isLoadingDetalle = false
            })
        },

        // ===== MÉTODOS DEL MODAL DE FORMULACIÓN =====

        // ---- Cuentas ----
        isCuentaSelected(cuenta) {
            return this.listadoCuentas.includes(cuenta?.cuenta)
        },

        toggleCuentaSelection(cuenta) {
            if (this.isVigenciaBloqueada) return
            if (!cuenta || !cuenta.cuenta) return
            const cuentaStr = String(cuenta.cuenta)
            const index = this.listadoCuentas.findIndex(item => String(item) === cuentaStr)
            if (index > -1) {
                this.listadoCuentas.splice(index, 1)
            } else {
                this.listadoCuentas.push(cuenta.cuenta)
            }
            this.updateSelectAllCuentas()
        },

        toggleSelectAllCuentas() {
            if (this.isVigenciaBloqueada) return
            this.selectAllCuentas = !this.selectAllCuentas
            const listadoCuentasStr = this.listadoCuentas.map(item => String(item))
            
            if (this.selectAllCuentas) {
                const allCodes = this.listCuentasSelMul.map(c => c?.cuenta).filter(code => code !== undefined && code !== null)
                allCodes.forEach(code => {
                    const codeStr = String(code)
                    if (!listadoCuentasStr.includes(codeStr)) {
                        this.listadoCuentas.push(code)
                    }
                })
            } else {
                const allCodes = this.listCuentasSelMul.map(c => c?.cuenta).filter(code => code !== undefined && code !== null)
                const codesStr = allCodes.map(c => String(c))
                this.listadoCuentas = this.listadoCuentas.filter(code => !codesStr.includes(String(code)))
            }
        },

        updateSelectAllCuentas() {
            if (this.listCuentasSelMul.length === 0) {
                this.selectAllCuentas = false
                return
            }
            const allCodes = this.listCuentasSelMul.map(c => c?.cuenta).filter(code => code !== undefined && code !== null)
            const listadoCuentasStr = this.listadoCuentas.map(item => String(item))
            this.selectAllCuentas = allCodes.every(code => listadoCuentasStr.includes(String(code)))
        },

        buscaCuenta() {
            if (this.critCuenta.length == 0) {
                this.listCuentasSelMul = [...this.listCuentas]
            } else {
                const criterio = "^" + this.critCuenta
                this.listCuentasSelMul = this.listCuentas.filter(
                    (cuenta) => cuenta.cuenta && cuenta.cuenta.match(criterio)
                )
            }
            this.updateSelectAllCuentas()
        },

        buscaDescripcion() {
            if (this.critDescripcion.length == 0) {
                this.listCuentasSelMul = [...this.listCuentas]
            } else {
                const searchTerm = this.critDescripcion.toUpperCase()
                this.listCuentasSelMul = this.listCuentas.filter(
                    (cuenta) => cuenta.descripcion && 
                        cuenta.descripcion.toUpperCase().includes(searchTerm)
                )
            }
            this.updateSelectAllCuentas()
        },

        consCuentas() {
            this.loadingCuentas = true
            PlanCuentas.getCuentasPorCompaVigRepor(
                this.companyId,
                this.selVigencia,
                this.selReporte
            )
            .then((res) => {
                this.listCuentas = res.data.planCuentas || []
                this.listCuentasSelMul = res.data.planCuentas || []
                this.loadingCuentas = false
                this.updateSelectAllCuentas()
            })
            .catch((err) => {
                console.log(err)
                this.listCuentas = []
                this.listCuentasSelMul = []
                this.loadingCuentas = false
            })
        },

        // ---- NIIF ----
        isNiifSelected(niif) {
            if (!niif || niif.niif === undefined || niif.niif === null) return false
            return this.detalleFormData.arrNiif.some(item => String(item) === String(niif.niif))
        },

        toggleNiifSelection(niif) {
            if (this.isVigenciaBloqueada) return
            if (!niif || niif.niif === undefined || niif.niif === null) return
            const niifStr = String(niif.niif)
            const index = this.detalleFormData.arrNiif.findIndex(item => String(item) === niifStr)
            if (index > -1) {
                this.detalleFormData.arrNiif.splice(index, 1)
            } else {
                this.detalleFormData.arrNiif.push(niif.niif)
            }
            this.updateSelectAllNiifs()
        },

        toggleSelectAllNiifs() {
            if (this.isVigenciaBloqueada) return
            this.selectAllNiifs = !this.selectAllNiifs
            const arrNiifStr = this.detalleFormData.arrNiif.map(item => String(item))
            
            if (this.selectAllNiifs) {
                const allCodes = this.listNiifFiltered.map(n => n?.niif).filter(code => code !== undefined && code !== null)
                allCodes.forEach(code => {
                    const codeStr = String(code)
                    if (!arrNiifStr.includes(codeStr)) {
                        this.detalleFormData.arrNiif.push(code)
                    }
                })
            } else {
                const allCodes = this.listNiifFiltered.map(n => n?.niif).filter(code => code !== undefined && code !== null)
                const codesStr = allCodes.map(c => String(c))
                this.detalleFormData.arrNiif = this.detalleFormData.arrNiif.filter(code => !codesStr.includes(String(code)))
            }
        },

        updateSelectAllNiifs() {
            if (this.listNiifFiltered.length === 0) {
                this.selectAllNiifs = false
                return
            }
            const allCodes = this.listNiifFiltered.map(n => n?.niif).filter(code => code !== undefined && code !== null)
            const arrNiifStr = this.detalleFormData.arrNiif.map(item => String(item))
            this.selectAllNiifs = allCodes.every(code => arrNiifStr.includes(String(code)))
        },

        filtraNiifs() {
            let filtered = [...this.listNiif]
            
            if (this.critNiifCode) {
                const codeFilter = this.critNiifCode.toUpperCase()
                filtered = filtered.filter(n => {
                    const code = n?.niif
                    if (code === undefined || code === null) return false
                    return String(code).toUpperCase().includes(codeFilter)
                })
            }
            
            if (this.critNiifName) {
                const nameFilter = this.critNiifName.toUpperCase()
                filtered = filtered.filter(n => {
                    const name = n?.nameNiif
                    if (name === undefined || name === null) return false
                    return String(name).toUpperCase().includes(nameFilter)
                })
            }
            
            this.listNiifFiltered = filtered
            this.updateSelectAllNiifs()
        },

        cargarNiif() {
            if (this.selVigencia == "") {
                this.$toast.error("El campo vigencia debe estar diligenciado")
                return
            }
            
            this.loadingNiifs = true
            
            PlanConsolidacion.getPlanConsPorCompVige(
                this.selVigencia,
                this.selReporte,
                this.companyId
            )
            .then((res) => {
                const niifData = res.data.planConsNiif || []
                this.listNiif = niifData
                this.listNiifFiltered = [...niifData]
                this.updateSelectAllNiifs()
                this.loadingNiifs = false
            })
            .catch((err) => {
                console.log(err)
                this.listNiif = []
                this.listNiifFiltered = []
                this.loadingNiifs = false
            })
        },

        actualizarVistaFormulacion(chkValue) {
            this.listNiif = []

            if (chkValue == 'n') {
                this.cargarNiif()
                this.showNiif = true
                this.showSumaFila = false
                this.showSumaColumna = false
                this.showCelda = false

                this.detalleFormData.sumFila = ''
                this.detalleFormData.sumColumna = ''
                this.detalleFormData.celFila = ''
                this.detalleFormData.celColumna = ''
            } else if (chkValue == 'sf') {
                this.showSumaFila = true
                this.showSumaColumna = false
                this.showCelda = false
                this.showNiif = false

                this.detalleFormData.arrNiif = []
                this.detalleFormData.sumColumna = ''
                this.detalleFormData.celFila = ''
                this.detalleFormData.celColumna = ''
            } else if (chkValue == 'sc') {
                this.showSumaColumna = true
                this.showSumaFila = false
                this.showCelda = false
                this.showNiif = false

                this.detalleFormData.arrNiif = []
                this.detalleFormData.sumFila = ''
                this.detalleFormData.celFila = ''
                this.detalleFormData.celColumna = ''
            } else if (chkValue == 'c') {
                this.showCelda = true
                this.showSumaFila = false
                this.showSumaColumna = false
                this.showNiif = false

                this.detalleFormData.arrNiif = []
                this.detalleFormData.sumFila = ''
                this.detalleFormData.sumColumna = ''
            } else if (chkValue == 'm' || chkValue == 'cons' || chkValue == 'ac') {
                this.showSumaFila = false
                this.showSumaColumna = false
                this.showNiif = false
                this.showCelda = false

                this.detalleFormData.arrNiif = []
                this.detalleFormData.sumFila = ''
                this.detalleFormData.sumColumna = ''
                this.detalleFormData.celFila = ''
                this.detalleFormData.celColumna = ''
            }
        },

        // ===== ABRIR MODAL DE FORMULACIÓN =====
        abrirModalCrearFormulacion() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            if (!this.filaSeleccionadaId) {
                this.$toast.error('Primero debe seleccionar una fila.')
                return
            }

            this.isEditingDetalle = false
            this.touchedDetalle = false

            // Resetear todos los datos del modal
            this.detalleFormData = {
                id: null,
                contable: null,
                actual: null,
                operacion: null,
                chk: '',
                sumFila: '',
                sumColumna: '',
                celFila: '',
                celColumna: '',
                arrNiif: [],
                cuentasSeleccionadas: []
            }

            // Resetear variables auxiliares
            this.listadoCuentas = []
            this.listCuentas = []
            this.listCuentasSelMul = []
            this.listNiif = []
            this.listNiifFiltered = []
            this.selectAllCuentas = false
            this.selectAllNiifs = false
            this.showCuenta = false
            this.showFormulacion = false
            this.showSumaFila = false
            this.showSumaColumna = false
            this.showCelda = false
            this.showNiif = false
            this.critCuenta = ''
            this.critDescripcion = ''
            this.critNiifCode = ''
            this.critNiifName = ''

            this.showModDetalle = true
        },

        // ===== GUARDAR FORMULACIÓN =====
        async guardarDetalle() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }

            // Validaciones
            if (this.detalleFormData.contable === null) {
                this.touchedDetalle = true
                this.$toast.warning('El campo contable es obligatorio')
                return
            }
            if (this.detalleFormData.actual === null) {
                this.touchedDetalle = true
                this.$toast.warning('El campo actual es obligatorio')
                return
            }
            if (!this.detalleFormData.operacion) {
                this.touchedDetalle = true
                this.$toast.warning('El campo operación es obligatorio')
                return
            }

            // Validar según contable
            if (this.detalleFormData.contable === 1 && this.listadoCuentas.length === 0) {
                this.$toast.error("Debe seleccionar al menos una cuenta")
                return
            }

            if (this.detalleFormData.contable === 0) {
                if (!this.detalleFormData.chk) {
                    this.$toast.error("Debe seleccionar un tipo de formulación")
                    return
                }
                if (this.detalleFormData.chk === 'n' && this.detalleFormData.arrNiif.length === 0) {
                    this.$toast.error("Debe seleccionar al menos un NIIF")
                    return
                }
            }

            const userId = this.$store.state.userId || localStorage.getItem('userId')
            if (!userId) {
                this.$toast.error('No se encontró el ID de usuario')
                return
            }

            this.isLoading = true

            try {
                let cuentasFinal = null
                let formulacionFinal = ''

                if (this.detalleFormData.contable === 1) {
                    // Contable = Sí: solo cuentas
                    cuentasFinal = this.listadoCuentas.join(',')
                    formulacionFinal = ''
                } else {
                    // Contable = No: construir la formulación según el tipo
                    const chk = this.detalleFormData.chk
                    if (chk === 'sf') {
                        formulacionFinal = `sf:${this.detalleFormData.sumFila}`
                    } else if (chk === 'sc') {
                        formulacionFinal = `sc:${this.detalleFormData.sumColumna}`
                    } else if (chk === 'c') {
                        formulacionFinal = `c:${this.detalleFormData.celFila},${this.detalleFormData.celColumna}`
                    } else if (chk === 'n') {
                        formulacionFinal = `n:${this.detalleFormData.arrNiif.join(',')}`
                    } else if (chk === 'm') {
                        formulacionFinal = 'm'
                    } else if (chk === 'cons') {
                        formulacionFinal = 'ac'
                    } else if (chk === 'ac') {
                        formulacionFinal = 'mc'
                    }
                }

                const payload = {
                    accion: this.isEditingDetalle ? 'UPDATE_FORMULACION' : 'INSERT_FORMULACION',
                    id: this.detalleFormData.id || null,
                    codReporte: parseInt(this.selReporte),
                    vigencia: this.selVigencia,
                    id_compania: parseInt(this.companyId),
                    orden: parseInt(this.filaSeleccionadaOrden),
                    contable: parseInt(this.detalleFormData.contable),
                    cuentas: cuentasFinal,
                    actual: parseInt(this.detalleFormData.actual),
                    operacion: this.detalleFormData.operacion,
                    formulacion: formulacionFinal,
                    u_crea: parseInt(userId),
                    u_modifica: parseInt(userId)
                }

                await Balance.gestionarFormulacionesOri(payload)
                this.$toast.success(this.isEditingDetalle ? 'Formulación actualizada' : 'Formulación creada')
                this.cerrarModalDetalle()
                await this.cargarDetalle()

            } catch (error) {
                console.error(error)
                this.$toast.error(error.response?.data?.message || 'Error al guardar')
            } finally {
                this.isLoading = false
            }
        },

        cerrarModalDetalle() {
            this.showModDetalle = false
            this.detalleFormData = {
                id: null,
                contable: null,
                actual: null,
                operacion: null,
                chk: '',
                sumFila: '',
                sumColumna: '',
                celFila: '',
                celColumna: '',
                arrNiif: [],
                cuentasSeleccionadas: []
            }
            this.touchedDetalle = false
            this.showCuenta = false
            this.showFormulacion = false
            this.showSumaFila = false
            this.showSumaColumna = false
            this.showCelda = false
            this.showNiif = false
            this.listadoCuentas = []
            this.listCuentas = []
            this.listCuentasSelMul = []
            this.listNiif = []
            this.listNiifFiltered = []
            this.selectAllCuentas = false
            this.selectAllNiifs = false
        },

        editDetalle(item) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }

            const detalleOriginal = this.detalleData.find(d => d.id === item.id)
            if (!detalleOriginal) {
                this.$toast.error('No se encontró la formulación')
                return
            }

            this.isEditingDetalle = true
            this.touchedDetalle = false
            this.prellenandoFormulario = true

            // Resetear variables auxiliares
            this.listadoCuentas = []
            this.listCuentas = []
            this.listCuentasSelMul = []
            this.listNiif = []
            this.listNiifFiltered = []
            this.selectAllCuentas = false
            this.selectAllNiifs = false
            this.critCuenta = ''
            this.critDescripcion = ''
            this.critNiifCode = ''
            this.critNiifName = ''

            // Parsear formulación según el tipo
            let chk = ''
            let sumFila = ''
            let sumColumna = ''
            let celFila = ''
            let celColumna = ''
            let arrNiif = []

            if (detalleOriginal.contable === 0 && detalleOriginal.formulacion) {
                const form = detalleOriginal.formulacion
                if (form.startsWith('sf:')) {
                    chk = 'sf'
                    sumFila = form.substring(3)
                } else if (form.startsWith('sc:')) {
                    chk = 'sc'
                    sumColumna = form.substring(3)
                } else if (form.startsWith('c:')) {
                    chk = 'c'
                    const coords = form.substring(2).split(',')
                    celFila = coords[0] || ''
                    celColumna = coords[1] || ''
                } else if (form.startsWith('n:')) {
                    chk = 'n'
                    arrNiif = form.substring(2).split(',').map(n => n.trim()).filter(n => n !== '')
                } else if (form === 'm') {
                    chk = 'm'
                } else if (form === 'ac') {
                    chk = 'cons'
                } else if (form === 'mc') {
                    chk = 'ac'
                }
            }

            this.detalleFormData = {
                id: detalleOriginal.id,
                contable: detalleOriginal.contable,
                actual: detalleOriginal.actual,
                operacion: detalleOriginal.operacion || '+',
                chk: chk,
                sumFila: sumFila,
                sumColumna: sumColumna,
                celFila: celFila,
                celColumna: celColumna,
                arrNiif: arrNiif,
                cuentasSeleccionadas: []
            }

            // Si es contable (1), cargar cuentas y seleccionar las existentes
            if (detalleOriginal.contable === 1) {
                this.showCuenta = true
                this.showFormulacion = false
                this.showSumaFila = false
                this.showSumaColumna = false
                this.showCelda = false
                this.showNiif = false

                // Cargar cuentas y seleccionar las que ya tiene
                this.loadingCuentas = true
                PlanCuentas.getCuentasPorCompaVigRepor(
                    this.companyId,
                    this.selVigencia,
                    this.selReporte
                )
                .then((res) => {
                    const cuentas = res.data.planCuentas || []
                    this.listCuentas = cuentas
                    this.listCuentasSelMul = cuentas

                    // Parsear cuentas existentes
                    if (detalleOriginal.cuenta) {
                        const cuentasExistentes = detalleOriginal.cuenta.split(',').map(c => c.trim()).filter(c => c !== '')
                        this.listadoCuentas = cuentasExistentes
                    }

                    this.loadingCuentas = false
                    this.updateSelectAllCuentas()
                })
                .catch((err) => {
                    console.log(err)
                    this.listCuentas = []
                    this.listCuentasSelMul = []
                    this.loadingCuentas = false
                })

            } else {
                // Es no contable (0)
                this.showCuenta = false
                this.showFormulacion = true

                // Mostrar según el tipo de formulación
                if (chk === 'sf') {
                    this.showSumaFila = true
                    this.showSumaColumna = false
                    this.showCelda = false
                    this.showNiif = false
                } else if (chk === 'sc') {
                    this.showSumaColumna = true
                    this.showSumaFila = false
                    this.showCelda = false
                    this.showNiif = false
                } else if (chk === 'c') {
                    this.showCelda = true
                    this.showSumaFila = false
                    this.showSumaColumna = false
                    this.showNiif = false
                } else if (chk === 'n') {
                    this.showNiif = true
                    this.showSumaFila = false
                    this.showSumaColumna = false
                    this.showCelda = false

                    // Cargamos NIIFs
                    this.loadingNiifs = true
                    PlanConsolidacion.getPlanConsPorCompVige(
                        this.selVigencia,
                        this.selReporte,
                        this.companyId
                    )
                    .then((res) => {
                        const niifData = res.data.planConsNiif || []

                        this.listNiif = niifData
                        this.listNiifFiltered = [...niifData]

                        // Marcar los NIIFs seleccionados
                        this.listNiifFiltered.forEach(niif => {
                            niif._selected = arrNiif.includes(String(niif.niif))
                        })

                        this.loadingNiifs = false
                        this.updateSelectAllNiifs()
                    })
                    .catch((err) => {
                        console.log(err)
                        this.listNiif = []
                        this.listNiifFiltered = []
                        this.loadingNiifs = false
                    })
                } else {
                    this.showSumaFila = false
                    this.showSumaColumna = false
                    this.showCelda = false
                    this.showNiif = false
                }
            }

            this.showModDetalle = true

            this.$nextTick(() => {
                this.prellenandoFormulario = false
            })
        },

        // ===== FILAS - CRUD =====
        openCreateFila() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            this.isEditingFila = false
            this.touchedFila = false
            this.filaFormData = { id: null, orden: null, descripcion: '' }
            this.showModFila = true
        },

        editFila(item) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            const filaOriginal = this.listFila.find(f => f.id === item.id)
            if (!filaOriginal) {
                this.$toast.error('No se encontró la fila')
                return
            }
            this.isEditingFila = true
            this.touchedFila = false
            this.filaFormData = {
                id: filaOriginal.id,
                orden: filaOriginal.orden,
                descripcion: filaOriginal.descripcion || ''
            }
            this.showModFila = true
        },

        async guardarFila() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para crear o editar.')
                return
            }
            if (!this.filaFormData.orden) {
                this.touchedFila = true
                this.$toast.warning('El orden es obligatorio')
                return
            }

            const userId = this.$store.state.userId || localStorage.getItem('userId')
            if (!userId) {
                this.$toast.error('No se encontró el ID de usuario')
                return
            }

            this.isLoading = true
            try {
                const payload = {
                    accion: this.isEditingFila ? 'UPDATE_FILA_PRINCIPAL' : 'INSERT_FILA_PRINCIPAL',
                    id: this.filaFormData.id || null,
                    codReporte: parseInt(this.selReporte),
                    vigencia: this.selVigencia,
                    id_compania: parseInt(this.companyId),
                    orden: parseInt(this.filaFormData.orden),
                    descripcion: this.filaFormData.descripcion || null,
                    u_crea: parseInt(userId),
                    u_modifica: parseInt(userId)
                }
                await Balance.gestionarFilasPrincipalesOri(payload)
                this.$toast.success(this.isEditingFila ? 'Fila actualizada' : 'Fila creada')
                this.cerrarModalFila()
                await this.cargarFilas()
            } catch (error) {
                console.error(error)
                this.$toast.error(error.response?.data?.message || 'Error al guardar')
            } finally {
                this.isLoading = false
            }
        },

        cerrarModalFila() {
            this.showModFila = false
            this.filaFormData = { id: null, orden: null, descripcion: '' }
            this.touchedFila = false
        },

        deleteFila(item) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.deleteId = item.id
            this.deleteTarget = 'fila'
            this.isDeleteAll = false
            this.showConfirmDeleteModal = true
        },

        borrarTodasFilas() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.deleteId = null
            this.deleteTarget = 'fila'
            this.isDeleteAll = true
            this.showConfirmDeleteModal = true
        },

        // ===== DETALLE (FORMULACIONES) - ELIMINAR =====
        deleteDetalle(item) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.deleteId = item.id
            this.deleteTarget = 'detalle'
            this.isDeleteAll = false
            this.showConfirmDeleteModal = true
        },

        borrarTodasFormulaciones() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error('La vigencia seleccionada está deshabilitada para eliminar.')
                return
            }
            this.deleteId = null
            this.deleteTarget = 'detalle'
            this.isDeleteAll = true
            this.showConfirmDeleteModal = true
        },

        closeConfirmDeleteModal() {
            this.showConfirmDeleteModal = false
            this.deleteId = null
            this.isDeleteAll = false
            this.deleteTarget = 'fila'
        },

        executeDelete() {
            if (this.deleteTarget === 'fila') {
                if (this.isDeleteAll) {
                    // Eliminar una por una todas las filas
                    const promesas = this.listFila.map(fila => 
                        Balance.eliminarFilaPrincipalOri(fila.id)
                    )
                    Promise.all(promesas)
                        .then(() => {
                            this.$toast.success('Todas las filas eliminadas.')
                            this.showConfirmDeleteModal = false
                            this.cargarFilas()
                        })
                        .catch((err) => {
                            console.error(err)
                            this.$toast.error('Error al eliminar las filas')
                        })
                } else {
                    Balance.eliminarFilaPrincipalOri(this.deleteId)
                        .then(() => {
                            this.$toast.success('Fila eliminada.')
                            this.showConfirmDeleteModal = false
                            this.cargarFilas()
                        })
                        .catch((err) => {
                            console.error(err)
                            this.$toast.error('Error al eliminar la fila')
                        })
                }
                } else if (this.deleteTarget === 'detalle') {
                    if (this.isDeleteAll) {
                        // Eliminar una por una todas las formulaciones de la fila
                        const promesas = this.detalleData.map(item => 
                            Balance.eliminarFormulacionOri(item.id)
                        )
                        Promise.all(promesas)
                            .then(() => {
                                this.$toast.success('Todas las formulaciones eliminadas.')
                                this.showConfirmDeleteModal = false
                                this.cargarDetalle()
                            })
                            .catch((err) => {
                                console.error(err)
                                this.$toast.error('Error al eliminar las formulaciones')
                            })
                    } else {
                        Balance.eliminarFormulacionOri(this.deleteId)
                            .then(() => {
                                this.$toast.success('Formulación eliminada.')
                                this.showConfirmDeleteModal = false
                                this.cargarDetalle()
                            })
                            .catch((err) => {
                                console.error(err)
                                this.$toast.error('Error al eliminar la formulación')
                            })
                    }
                }
        },

        // ===== UTILIDADES =====
        getDetalleTexto(item) {
            const es = this.$store.state._language == "es-ES"
            
            if (item.contable === 1) {
                if (item.cuenta) {
                    return es ? `Cuentas: ${item.cuenta}` : `Accounts: ${item.cuenta}`
                }
                if (item.niif) {
                    return es ? `NIIF: ${item.niif}` : `IFRS: ${item.niif}`
                }
                return '-'
            } else {
                return this.getFormulacionTexto(item)
            }
        },

        getFormulacionTexto(item) {
            const es = this.$store.state._language == "es-ES"
            
            if (!item.formulacion) return es ? 'Valor Manual' : 'Manual Value'
            
            if (item.formulacion === 'm') {
                return es ? 'Valor Manual' : 'Manual Value'
            }
            if (item.formulacion === 'ac') {
                return es ? 'Ajuste de Consolidación' : 'Consolidation Adjustment'
            }
            if (item.formulacion === 'mc') {
                return es ? 'Ajuste Combinado' : 'Combined Adjustment'
            }
            if (item.formulacion.startsWith('sf:')) {
                const filas = item.formulacion.substring(3)
                return es ? `Suma Fila(s): ${filas}` : `Sum Row(s): ${filas}`
            }
            if (item.formulacion.startsWith('sc:')) {
                const columnas = item.formulacion.substring(3)
                return es ? `Suma Columna(s): ${columnas}` : `Sum Column(s): ${columnas}`
            }
            if (item.formulacion.startsWith('c:')) {
                const coords = item.formulacion.substring(2)
                return es ? `Celda: ${coords}` : `Cell: ${coords}`
            }
            if (item.formulacion.startsWith('n:')) {
                const niifs = item.formulacion.substring(2)
                return es ? `NIIF: ${niifs}` : `IFRS: ${niifs}`
            }
            return es ? 'Manual' : 'Manual'
        },

        truncarTexto(texto, longitud) {
            if (!texto) return '-'
            return texto.length > longitud ? texto.substring(0, longitud) + '...' : texto
        },

        cargarDatosIniciales() {
            this.isLoading = true

            // Determinar si es administrador por la ruta
            if (this.$route.name == "oriReporteADM") {
                this.administrador = true
            }

            Promise.all([
                Balance.leaCompaniaUsuario(),
                PlanCuentas.getVigenciaPlancuentas(),
                Company.getAllCompanies(),
                Balance.getReportes(),
                Grupo.getAllGrupos()
            ])
                .then(([companiaRes, vigenciaRes, companyRes, reportesRes, grupoRes]) => {
                    this.companiaUsuario = companiaRes.data.companiaUsuario || []
                    this.listVigencias = vigenciaRes.data.getVigencia || []
                    this.listReportes = reportesRes.data.reportes || []
                    this.listGrupos = grupoRes.data.grupos || []

                    let v_companias = companyRes.data.companies || []
                    if (this.$store.state.userRole != 1) {
                        v_companias = v_companias.filter(element => {
                            return this.companiaUsuario.some(
                                r_usuario => r_usuario.id_usuario == this.$store.state.userId && r_usuario.id_compania == element.id
                            )
                        })
                    }
                    this.listCompanies = v_companias
                    this.filteredCompanies = [...v_companias]
                })
                .catch((err) => {
                    console.error(err)
                    this.$toast.error('Error al cargar los datos iniciales')
                })
                .finally(() => {
                    this.isLoading = false
                })
        }
    },

    mounted() {
        this.cargarDatosIniciales()
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
/* ========== ESTILOS DE FILTROS PRINCIPALES ========== */
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.niif-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover,
.niif-header .consult-btn:focus,
.niif-header .consult-btn:active,
.niif-header .consult-btn:not(:disabled):hover,
.niif-header .consult-btn:not(:disabled):focus,
.niif-header .consult-btn:not(:disabled):active {
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
    box-shadow: none !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    background-color: #6c757d !important;
    color: white !important;
    border-color: #6c757d !important;
}

/* ========== ESTILOS DE FLECHA Y TÍTULO ========== */
.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

/* ========== TOOLBAR ========== */
.niif-toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 0 0 auto;
}

.table-toolbar-add-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #28a745;
    color: white;
    border: 1px solid #28a745;
}

.table-toolbar-add-btn:hover:not(:disabled) {
    background-color: #218838;
    border-color: #1e7e34;
}

.table-toolbar-add-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* ========== BOTONES DE ACCIÓN EN TABLA ========== */
.table-action-btn {
    border: none;
    border-radius: 4px;
    transition: all 0.2s;
}

.table-action-btn.btn-info {
    background-color: #17a2b8;
    color: white;
}

.table-action-btn.btn-info:hover:not(:disabled) {
    background-color: #138496;
}

.table-action-btn.btn-info:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* ========== ESTILOS DEL MODAL DE FORMULACIÓN ========== */
.uniform-modal-body {
    padding: 8px 4px 4px 4px;
}

.uniform-form-row {
    margin-bottom: 12px;
}

.uniform-label-col label {
    margin-bottom: 0;
    font-weight: 500;
    font-size: 14px;
}

.form-input {
    width: 100%;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.password-policy-box {
    background: white;
    border-radius: 8px;
    padding: 16px 20px;
    border-left: 4px solid #2e507a;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.password-policy-title {
    font-weight: 600;
    font-size: 15px;
    color: #2e507a;
    margin-bottom: 4px;
}

.password-policy-subtitle {
    font-size: 13px;
    color: #6c757d;
}

.uniform-actions-wrap {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;
}

.reset-btn-primary {
    background-color: #2e507a !important;
    border: 1px solid #2e507a !important;
    color: white !important;
    padding: 8px 24px;
    border-radius: 6px;
    font-weight: 500;
}

.reset-btn-primary:hover:not(:disabled) {
    background-color: #1f3f62 !important;
    border-color: #1f3f62 !important;
}

.reset-btn-primary:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.reset-btn-secondary {
    background-color: #f8f9fa !important;
    border: 1px solid #ced4da !important;
    color: #495057 !important;
    padding: 8px 24px;
    border-radius: 6px;
    font-weight: 500;
}

.reset-btn-secondary:hover:not(:disabled) {
    background-color: #e9ecef !important;
    border-color: #adb5bd !important;
}

.reset-modal-header {
    background-color: #2e507a;
    color: white;
    padding: 16px 20px;
    border-radius: 8px 8px 0 0;
}

.reset-modal-title {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
}

.reset-modal-body {
    padding: 24px 20px;
    background-color: #f8f9fa;
}

/* ========== CRITERIO DE BÚSQUEDA ========== */
.criterio-inputs-wrapper {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
}

.accounts-section {
    margin-top: 8px;
}

.select-all-container {
    padding: 4px 0;
}

/* ========== TABLA SCROLLABLE ========== */
.custom-scroll-table {
    height: 200px !important;
    min-height: 200px !important;
    max-height: 200px !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    display: block !important;
    position: relative !important;
}

.custom-scroll-table table {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
    margin-bottom: 0 !important;
    border-collapse: separate !important;
    border-spacing: 0 !important;
}

.custom-scroll-table table thead {
    display: table-header-group !important;
    width: 100% !important;
    position: sticky !important;
    top: 0 !important;
    z-index: 10 !important;
}

.custom-scroll-table table thead tr {
    display: table-row !important;
    width: 100% !important;
}

.custom-scroll-table table thead th {
    background-color: #f8f9fa !important;
    border-bottom: 2px solid #dee2e6 !important;
    padding: 6px 8px !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    color: #495057 !important;
    position: sticky !important;
    top: 0 !important;
    z-index: 10 !important;
    text-overflow: ellipsis !important;
    overflow: hidden !important;
    white-space: nowrap !important;
}

.custom-scroll-table table thead th:first-child {
    width: 120px !important;
    min-width: 120px !important;
    max-width: 120px !important;
}

.custom-scroll-table table thead th:last-child {
    width: auto !important;
}

.custom-scroll-table table tbody {
    display: table-row-group !important;
    width: 100% !important;
}

.custom-scroll-table table tbody tr {
    display: table-row !important;
    width: 100% !important;
    cursor: pointer !important;
    border-radius: 8px !important;
}

.custom-scroll-table table tbody td {
    padding: 5px 8px !important;
    border: none !important;
    text-overflow: ellipsis !important;
    overflow: hidden !important;
    white-space: nowrap !important;
    vertical-align: middle !important;
}

.custom-scroll-table table tbody td:first-child {
    width: 120px !important;
    min-width: 120px !important;
    max-width: 120px !important;
    border-radius: 8px 0 0 8px !important;
}

.custom-scroll-table table tbody td:last-child {
    width: auto !important;
    border-radius: 0 8px 8px 0 !important;
}

.custom-scroll-table table tbody tr:hover td {
    background-color: #f5f9ff !important;
}

.custom-scroll-table::-webkit-scrollbar {
    width: 8px !important;
}

.custom-scroll-table::-webkit-scrollbar-track {
    background: #f1f1f1 !important;
    border-radius: 4px !important;
}

.custom-scroll-table::-webkit-scrollbar-thumb {
    background: #c1c1c1 !important;
    border-radius: 4px !important;
}

.custom-scroll-table::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8 !important;
}

.table-active-blue {
    background-color: #e3f0ff !important;
    border-radius: 8px !important;
}

.table-active-blue td {
    background-color: #e3f0ff !important;
}

.table-active-blue td:first-child {
    border-radius: 8px 0 0 8px !important;
}

.table-active-blue td:last-child {
    border-radius: 0 8px 8px 0 !important;
}

/* ========== CHECKBOX NATIVOS ========== */
.custom-checkbox-wrapper {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
}

.custom-checkbox-wrapper input[type="radio"] {
    width: 16px;
    height: 16px;
    accent-color: #2e507a;
    cursor: pointer;
    margin: 0;
    flex-shrink: 0;
}

.custom-checkbox-wrapper input[type="radio"]:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.custom-checkbox-wrapper label {
    margin: 0;
    font-size: 14px;
    font-weight: 400;
    color: #495057;
    cursor: pointer;
}

.custom-checkbox-wrapper input[type="radio"]:disabled + label {
    cursor: not-allowed;
    opacity: 0.6;
}

.gap-2 {
    gap: 8px;
}

.gap-3 {
    gap: 12px;
}

/* ========== RESPONSIVE ========== */
@media (max-width: 1200px) {
    .niif-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }
    
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }
    
    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }
    
    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 768px) {
    .custom-scroll-table {
        height: 160px !important;
        min-height: 160px !important;
        max-height: 160px !important;
    }

    .custom-scroll-table table thead th:first-child,
    .custom-scroll-table table tbody td:first-child {
        width: 80px !important;
        min-width: 80px !important;
        max-width: 80px !important;
        font-size: 11px !important;
    }

    .custom-scroll-table table tbody td {
        font-size: 12px !important;
        padding: 4px 6px !important;
    }

    .custom-scroll-table table thead th {
        font-size: 11px !important;
        padding: 4px 6px !important;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }
    
    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }
    
    .niif-header .filters-container {
        gap: 4px;
    }
    
    .niif-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
    }

    .password-policy-box {
        padding: 12px 16px;
    }
    
    .password-policy-title {
        font-size: 14px;
    }
    
    .password-policy-subtitle {
        font-size: 13px;
    }
    
    .reset-dialog-actions {
        flex-direction: column;
    }
    
    .reset-dialog-actions .reset-btn-secondary,
    .reset-dialog-actions .reset-btn-primary {
        width: 100%;
        margin: 4px 0 !important;
    }

    .uniform-label-col {
        flex: 0 0 100%;
        max-width: 100%;
        margin-bottom: 4px;
        text-align: left !important;
    }
    
    .uniform-form-row .col-md-8 {
        flex: 0 0 100%;
        max-width: 100%;
    }

    .custom-scroll-table {
        height: 140px !important;
        min-height: 140px !important;
        max-height: 140px !important;
    }

    .custom-scroll-table table thead th:first-child,
    .custom-scroll-table table tbody td:first-child {
        width: 60px !important;
        min-width: 60px !important;
        max-width: 60px !important;
        font-size: 10px !important;
    }

    .custom-scroll-table table tbody td {
        font-size: 11px !important;
        padding: 3px 4px !important;
    }

    .custom-scroll-table table thead th {
        font-size: 10px !important;
        padding: 3px 4px !important;
    }

    .custom-checkbox-wrapper {
        gap: 4px;
    }

    .custom-checkbox-wrapper input[type="radio"] {
        width: 14px;
        height: 14px;
    }

    .custom-checkbox-wrapper label {
        font-size: 13px;
    }

    .gap-3 {
        gap: 8px;
    }
}
</style>